import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { pl } from 'date-fns/locale';
import { collection, addDoc, doc, updateDoc, getDocs, query, where, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { AuthContext } from '../../contexts/AuthContext';

const AddPurchaseModal = ({ open, handleClose, clientId, clientName, onPurchaseAdded }) => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [purchase, setPurchase] = useState({
    date: new Date(),
    productId: '',
    productName: '',
    amount: '',
    paymentMethod: 'cash',
    notes: ''
  });

  // Pobierz produkty przy otwarciu modalu
  useEffect(() => {
    const fetchProducts = async () => {
      if (!open) return;
      
      setLoadingProducts(true);
      try {
        const productsRef = collection(db, 'products');
        const productsQuery = query(productsRef, where('status', '==', 'available'));
        const querySnapshot = await getDocs(productsQuery);
        
        const productsList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setProducts(productsList);
      } catch (error) {
        console.error('Błąd podczas pobierania produktów:', error);
      } finally {
        setLoadingProducts(false);
      }
    };
    
    fetchProducts();
  }, [open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPurchase(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (newDate) => {
    setPurchase(prev => ({
      ...prev,
      date: newDate
    }));
  };

  const handleProductChange = (event, newValue) => {
    if (newValue) {
      setPurchase(prev => ({
        ...prev,
        productId: newValue.id,
        productName: getProductDisplayName(newValue),
        amount: newValue.price || 0
      }));
    } else {
      setPurchase(prev => ({
        ...prev,
        productId: '',
        productName: '',
        amount: ''
      }));
    }
  };

  const getProductDisplayName = (product) => {
    if (product.category === 'watch') {
      return `${product.brand} ${product.model} (${product.reference || 'Brak ref.'})`;
    } else if (product.category === 'jewelry') {
      return `${product.brand} ${product.jewelry_name || 'Biżuteria'} (${product.reference || 'Brak ref.'})`;
    } else {
      return product.product_name || 'Produkt';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(price);
  };

  const handleAddPurchase = async () => {
    try {
      setLoading(true);

      // Pobierz aktualne dane klienta z Firestore, aby prawidłowo zaktualizować totalSpent
      const clientRef = doc(db, 'clients', clientId);
      const clientSnap = await getDoc(clientRef);
      let clientData = {};
      
      if (clientSnap.exists()) {
        clientData = clientSnap.data();
      } else {
        throw new Error('Nie znaleziono wybranego klienta');
      }

      // Przygotuj dane zakupu - ujednolicone z ProductDetails.js
      const salesData = {
        ...purchase,
        clientId,
        clientName,
        employeeId: currentUser.uid,
        employeeName: currentUser.displayName || currentUser.email,
        transactionDate: purchase.date || new Date(),
        createdAt: new Date(),
        lastUpdate: new Date(),
        source: 'client_purchase',
        totalAmount: parseFloat(purchase.amount) || 0
      };

      // Dodaj dokument zakupu do kolekcji sales
      const salesRef = collection(db, 'sales');
      const saleDocRef = await addDoc(salesRef, salesData);
      console.log('Wpis sprzedaży dodany z ID:', saleDocRef.id);

      // Zaktualizuj datę ostatniego zakupu i łączną kwotę wydaną przez klienta
      const amount = parseFloat(purchase.amount) || 0;
      const currentTotalSpent = parseFloat(clientData.totalSpent) || 0;
      const newTotalSpent = currentTotalSpent + amount;
      
      await updateDoc(clientRef, {
        lastPurchaseDate: purchase.date || new Date(),
        totalSpent: newTotalSpent,
        updated_at: new Date(),
        updated_by: {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || ''
        }
      });

      // Zaktualizuj status produktu na sprzedany
      if (purchase.productId) {
        await updateDoc(doc(db, 'products', purchase.productId), {
          status: 'sold',
          sold_to: clientId,
          sold_date: purchase.date,
          updated_at: new Date(),
          updated_by: {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName || ''
          }
        });
      }

      // Wywołaj callback
      if (onPurchaseAdded) {
        onPurchaseAdded({
          id: saleDocRef.id,
          ...salesData
        });
      }

      // Zamknij modal
      handleClose();
    } catch (error) {
      console.error('Błąd podczas dodawania zakupu:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Dodaj zakup klienta</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Klient: {clientName}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
              <DatePicker
                label="Data zakupu"
                value={purchase.date}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Metoda płatności</InputLabel>
              <Select
                name="paymentMethod"
                value={purchase.paymentMethod}
                onChange={handleInputChange}
                label="Metoda płatności"
              >
                <MenuItem value="cash">Gotówka</MenuItem>
                <MenuItem value="card">Karta</MenuItem>
                <MenuItem value="transfer">Przelew</MenuItem>
                <MenuItem value="other">Inna</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Autocomplete
              options={products}
              getOptionLabel={(option) => getProductDisplayName(option)}
              loading={loadingProducts}
              onChange={handleProductChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Produkt"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Kwota"
              name="amount"
              type="number"
              value={purchase.amount}
              onChange={handleInputChange}
              required
              InputProps={{
                startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>PLN</Typography>,
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notatki"
              name="notes"
              value={purchase.notes}
              onChange={handleInputChange}
              multiline
              rows={4}
              placeholder="Dodatkowe informacje o zakupie..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Anuluj</Button>
        <Button 
          onClick={handleAddPurchase} 
          variant="contained" 
          disabled={loading || !purchase.amount || !purchase.productName}
        >
          {loading ? <CircularProgress size={24} /> : 'Dodaj zakup'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPurchaseModal;
